import { z } from "zod";
// ==================== Enums ====================
export const PayRuleTypeSchema = z.enum(["FLAT_AMOUNT", "MULTIPLIER"]);
export const PayRuleUnitSchema = z.enum(["PER_HOUR", "PER_DAY"]);
// ==================== Condition Schemas ====================
// Time format validation helper
const timeStringSchema = z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, {
    message: "Time must be in HH:mm format (24-hour)",
});
// Condition type 1: DAY_OF_WEEK
const DayOfWeekConditionSchema = z.object({
    type: z.literal("DAY_OF_WEEK"),
    days: z.array(z.enum([
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "SATURDAY",
        "SUNDAY",
    ])),
});
// Condition type 2: WEEKENDS_ONLY
const WeekendsOnlyConditionSchema = z.object({
    type: z.literal("WEEKENDS_ONLY"),
    enabled: z.boolean(),
});
// Condition type 3: SHIFT_STARTS_AFTER
const ShiftStartsAfterConditionSchema = z.object({
    type: z.literal("SHIFT_STARTS_AFTER"),
    time: timeStringSchema,
});
// Condition type 4: SHIFT_ENDS_AFTER
const ShiftEndsAfterConditionSchema = z.object({
    type: z.literal("SHIFT_ENDS_AFTER"),
    time: timeStringSchema,
});
// Condition type 5: SHIFT_STARTS_BEFORE
const ShiftStartsBeforeConditionSchema = z.object({
    type: z.literal("SHIFT_STARTS_BEFORE"),
    time: timeStringSchema,
});
// Condition type 6: SHIFT_ENDS_BEFORE
const ShiftEndsBeforeConditionSchema = z.object({
    type: z.literal("SHIFT_ENDS_BEFORE"),
    time: timeStringSchema,
});
// Condition type 7: SHIFT_DURATION_EXCEEDS
const ShiftDurationExceedsConditionSchema = z.object({
    type: z.literal("SHIFT_DURATION_EXCEEDS"),
    minutes: z.number().int().positive(),
});
// Condition type 8: DATE_RANGE
const DateRangeConditionSchema = z.object({
    type: z.literal("DATE_RANGE"),
    startDate: z.string().date(), // ISO 8601 date (YYYY-MM-DD)
    endDate: z.string().date(),
});
// Condition type 9: COMPANY_HOLIDAY
const CompanyHolidayConditionSchema = z.object({
    type: z.literal("COMPANY_HOLIDAY"),
    holidayIds: z.array(z.uuid()),
});
// Condition type 10: AD_HOC
const AdHocConditionSchema = z.object({
    type: z.literal("AD_HOC"),
    enabled: z.boolean(),
});
// Discriminated union for all condition types
export const PayRuleConditionSchema = z.discriminatedUnion("type", [
    DayOfWeekConditionSchema,
    WeekendsOnlyConditionSchema,
    ShiftStartsAfterConditionSchema,
    ShiftEndsAfterConditionSchema,
    ShiftStartsBeforeConditionSchema,
    ShiftEndsBeforeConditionSchema,
    ShiftDurationExceedsConditionSchema,
    DateRangeConditionSchema,
    CompanyHolidayConditionSchema,
    AdHocConditionSchema,
]);
// Array of conditions (all use AND logic)
export const PayRuleConditionsSchema = z.array(PayRuleConditionSchema);
// ==================== Input Schemas ====================
export const CreatePayRuleInputSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().optional(),
    jobRoleIds: z.array(z.uuid()).default([]),
    locationIds: z.array(z.uuid()).default([]),
    payAmount: z.number().positive(),
    payType: PayRuleTypeSchema,
    payUnit: PayRuleUnitSchema.optional(),
    conditions: PayRuleConditionsSchema,
    isActive: z.boolean().default(true),
    priority: z.number().int().default(0),
});
export const UpdatePayRuleInputSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().optional(),
    jobRoleIds: z.array(z.uuid()).optional(),
    locationIds: z.array(z.uuid()).optional(),
    payAmount: z.number().positive().optional(),
    payType: PayRuleTypeSchema.optional(),
    payUnit: PayRuleUnitSchema.optional(),
    conditions: PayRuleConditionsSchema.optional(),
    isActive: z.boolean().optional(),
    priority: z.number().int().optional(),
});
// ==================== Response Schemas ====================
export const PayRuleResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    jobRoleIds: z.array(z.uuid()),
    locationIds: z.array(z.uuid()),
    payAmount: z.number(),
    payType: PayRuleTypeSchema,
    payUnit: PayRuleUnitSchema.nullable(),
    conditions: PayRuleConditionsSchema,
    isActive: z.boolean(),
    priority: z.number(),
    companyId: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const ListPayRulesResponseSchema = z.array(PayRuleResponseSchema);
